"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { useRouter } from "next/navigation"
import { useState } from "react"
import { useForm } from "react-hook-form"
import { completeTriiboRegistration } from "@/api/auth"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { resolvePostLoginRedirect } from "@/utilities/auth-callback"

const AUTH_ME_QUERY_KEY = ["auth", "me"] as const
const CART_ITEMS_QUERY_KEY = ["cart", "items"] as const
const AUTH_SYNC_ATTEMPTS = 5
const AUTH_SYNC_WAIT_MS = 150

interface AuthMeResponse {
  user: {
    name?: string
  } | null
}

interface RegistrationFormValues {
  email: string
  password: string
  terms: boolean
}

interface PendingRegistrationFormProps {
  cellphone: string
  triiboId: string
  callbackParam: string | null
  onAuthenticated: (payload: {
    apiRedirect?: string | null
    shouldSyncCart: boolean
  }) => Promise<void>
}

const wait = async (milliseconds: number): Promise<void> => {
  await new Promise((resolve) => {
    setTimeout(resolve, milliseconds)
  })
}

export const PendingRegistrationForm = ({
  cellphone,
  triiboId,
  callbackParam,
  onAuthenticated,
}: PendingRegistrationFormProps) => {
  const router = useRouter()
  const queryClient = useQueryClient()
  const [apiError, setApiError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<RegistrationFormValues>({
    defaultValues: {
      email: "",
      password: "",
      terms: false,
    },
  })

  const completeRegistrationMutation = useMutation({
    mutationFn: async (payload: RegistrationFormValues) => {
      return await completeTriiboRegistration({
        cellphone,
        email: payload.email,
        password: payload.password,
        terms: payload.terms,
        triiboId,
      })
    },
  })

  const syncPostLoginQueries = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: AUTH_ME_QUERY_KEY,
    })

    for (let attempt = 0; attempt < AUTH_SYNC_ATTEMPTS; attempt += 1) {
      const authMeData =
        queryClient.getQueryData<AuthMeResponse>(AUTH_ME_QUERY_KEY)

      if (authMeData?.user) {
        break
      }

      await queryClient.refetchQueries({
        queryKey: AUTH_ME_QUERY_KEY,
      })

      await wait(AUTH_SYNC_WAIT_MS)
    }

    await queryClient.invalidateQueries({
      queryKey: CART_ITEMS_QUERY_KEY,
    })

    await queryClient.refetchQueries({
      queryKey: CART_ITEMS_QUERY_KEY,
    })
  }

  const onSubmit = async (values: RegistrationFormValues) => {
    setApiError(null) // Clear previous errors
    const response = await completeRegistrationMutation
      .mutateAsync(values)
      .catch(() => null)

    if (!response) {
      setApiError("Falha ao completar cadastro.")
      return
    }

    if (!response.success) {
      setApiError(
        response.error?.message || "Nao foi possivel completar cadastro."
      )
      return
    }

    await syncPostLoginQueries()

    await onAuthenticated({
      apiRedirect: response.redirect,
      shouldSyncCart: true,
    })

    const redirectUrl = resolvePostLoginRedirect({
      callback: callbackParam,
      apiRedirect: response.redirect,
      fallback: "/minha-conta",
    })

    router.push(redirectUrl)
  }

  return (
    <div className='space-y-3 rounded-md border bg-gray-50 p-3'>
      <p className='text-sm'>
        Usuario ainda nao vinculado. Informe email e senha para continuar o
        cadastro.
      </p>

      <form className='space-y-3' onSubmit={handleSubmit(onSubmit)}>
        <Input
          {...register("email", {
            required: "E-mail e obrigatorio.",
          })}
          placeholder='seu@email.com'
          type='email'
        />
        {errors.email ? (
          <p className='text-red-600 text-sm'>{errors.email.message}</p>
        ) : null}

        <Input
          {...register("password", {
            required: "Senha e obrigatoria.",
          })}
          placeholder='Senha'
          type='password'
        />
        {errors.password ? (
          <p className='text-red-600 text-sm'>{errors.password.message}</p>
        ) : null}

        <label className='flex items-center gap-2 text-sm'>
          <input
            {...register("terms", {
              required: "Voce precisa aceitar os termos de uso.",
            })}
            type='checkbox'
          />
          Aceito os termos de uso
        </label>
        {errors.terms ? (
          <p className='text-red-600 text-sm'>{errors.terms.message}</p>
        ) : null}

        <p className='text-muted-foreground text-xs'>
          Triibo ID identificado: {triiboId}
        </p>

        <Button
          className='w-full'
          disabled={completeRegistrationMutation.isPending}
          type='submit'
        >
          {completeRegistrationMutation.isPending
            ? "Finalizando cadastro..."
            : "Completar cadastro"}
        </Button>

        {apiError ? <p className='text-red-600 text-sm'>{apiError}</p> : null}
      </form>
    </div>
  )
}
